import { createSlice } from "@reduxjs/toolkit";
import { reconcileColumns } from "./columns/reconcileColumns";
import { getColumnPolicy } from "./columns/columnPolicy";

// Datakeys that must always remain in the trailing position (e.g. the row-action
// "add comment" column), regardless of any saved/drag order.
const TERMINAL_DATAKEYS = ["add_comment"];

const createInitialTableState = () => ({
  visibleColumns: [],
  customColumns: [],
  allColumns: [],
  initialized: false,
});

const initialState = {
  // Configuration per table type
  vacancy: createInitialTableState(),
  lease: createInitialTableState(),
  delinquency: createInitialTableState(),
  LeaseUp: createInitialTableState(),
  vacancy_analysis: createInitialTableState(),
  vacancy_analysis_full: createInitialTableState(),
  lease_renewal_analysis: createInitialTableState(),
  delinquency_analysis: createInitialTableState(),
  drilldown_vacancy: createInitialTableState(),
  drilldown_lease: createInitialTableState(),
  drilldown_floorplan: createInitialTableState(),
  manager_review: createInitialTableState(),
  unit_details: createInitialTableState(),
  work_order_labor_summary: createInitialTableState(),
  work_order_labor_summary_investor: createInitialTableState(),
  // Global settings
  settings: {
    rememberColumnState: true,
    defaultShowAll: true,
  },
};

const reorderColumnsInternally = (allColumns) => {
  const nonTerminal = [];
  const terminal = [];

  allColumns.forEach((col) => {
    if (TERMINAL_DATAKEYS.includes(col.datakey)) {
      terminal.push(col);
    } else {
      nonTerminal.push(col);
    }
  });

  return [...nonTerminal, ...terminal];
};

// ── Legacy (pre-fix) order construction ──────────────────────────────────────
// Faithful reproduction of the original initializeColumns ordering logic, used by
// tables whose policy does NOT preserve user order. Static columns follow the
// code-defined `settings` order (authoritative), then existing custom columns are
// appended, then terminal columns are snapped to the end. The Array.isArray guards
// are the only addition — they prevent crashes on malformed/empty persisted state
// and do not change behavior for the valid array inputs the reducer always passes.
const buildLegacyColumnOrder = (settings, persistedAllColumns) => {
  const safeSettings = Array.isArray(settings) ? settings : [];
  const existing = Array.isArray(persistedAllColumns)
    ? persistedAllColumns
    : [];

  const nextAllColumns = [];
  const processedKeys = new Set();

  // Static columns in the order defined by settings (refresh definition from code).
  safeSettings.forEach((col) => {
    const existingCol = existing.find((c) => c && c.datakey === col.datakey);
    if (existingCol) {
      nextAllColumns.push({ ...existingCol, ...col });
    } else {
      nextAllColumns.push(col);
    }
    processedKeys.add(col.datakey);
  });

  // Then append custom columns that are not part of static settings.
  existing.forEach((col) => {
    if (col && col.isCustom && !processedKeys.has(col.datakey)) {
      nextAllColumns.push(col);
      processedKeys.add(col.datakey);
    }
  });

  return reorderColumnsInternally(nextAllColumns);
};

// Faithful reproduction of the original setCustomColumns ordering: keep static
// columns in place, drop all old customs, append the new custom set at the end,
// then snap terminal columns to the end.
const appendLegacyCustomColumns = (persistedAllColumns, newCustomColumns) => {
  const existing = Array.isArray(persistedAllColumns)
    ? persistedAllColumns
    : [];
  const customs = Array.isArray(newCustomColumns) ? newCustomColumns : [];
  const staticColumns = existing.filter((c) => c && !c.isCustom);
  return reorderColumnsInternally([...staticColumns, ...customs]);
};

export const columnsSlice = createSlice({
  name: "columns",
  initialState,
  reducers: {
    /**
     * Initialize column configuration for a specific table type
     * Sets up all available columns and marks them as visible by default
     */
    initializeColumns: (state, action) => {
      const { tableType, settings } = action.payload;

      if (!state[tableType]) {
        state[tableType] = createInitialTableState();
      }

      const tableState = state[tableType];
      const settingsMap = new Map(settings.map((s) => [s.datakey, s]));
      const previousKeys = new Set(tableState.allColumns.map((c) => c.datakey));
      const currentVisible = new Set(tableState.visibleColumns);

      // 1. Build allColumns according to this table's order policy.
      // preserveStaticOrder (e.g. vacancy_analysis_full): the user's persisted
      // ORDER is authoritative across remount/navigation/refresh; current settings
      // are merged on top for behavioral metadata, new columns appended, removed
      // ones dropped. Legacy (default for every other table, incl. Property View
      // drill-downs): rebuild from the code-defined `settings` order — the exact
      // pre-fix behavior. Visibility handling below is shared by both paths.
      const policy = getColumnPolicy(tableType);
      tableState.allColumns = policy.preserveStaticOrder
        ? reconcileColumns({
            currentColumns: settings,
            persistedColumns: tableState.allColumns,
            terminalColumnKeys: TERMINAL_DATAKEYS,
            preserveCustom: true,
          })
        : buildLegacyColumnOrder(settings, tableState.allColumns);

      // 2. Update visibleColumns
      const nextVisibleColumns = [];

      tableState.allColumns.forEach((col) => {
        const isMandatory = col.isMandatory;
        const isNew = !previousKeys.has(col.datakey);
        const isPredefined = settingsMap.has(col.datakey);
        const codeHidden = isPredefined
          ? settingsMap.get(col.datakey).hidden
          : false;

        if (isMandatory) {
          nextVisibleColumns.push(col.datakey);
        } else if (isNew) {
          // If it's a brand new column from code or custom, and it's NOT explicitly hidden in code, show it.
          // Note: custom columns added via UI aren't in 'settings', so we check 'isCustom' too.
          if (
            !codeHidden &&
            !(tableType === "manager_review" && !isPredefined)
          ) {
            nextVisibleColumns.push(col.datakey);
          }
        } else {
          // Existing column: respect persisted visibility from Redux
          if (currentVisible.has(col.datakey)) {
            nextVisibleColumns.push(col.datakey);
          }
        }
      });

      tableState.visibleColumns = nextVisibleColumns;
      tableState.initialized = true;
    },

    /**
     * Toggle visibility of a single column
     */
    toggleColumnVisibility: (state, action) => {
      const { tableType, datakey } = action.payload;

      if (!state[tableType]) {
        console.error(`Invalid table type: ${tableType}`);
        return;
      }

      const visibleColumns = state[tableType].visibleColumns;
      const index = visibleColumns.indexOf(datakey);

      if (index > -1) {
        // Column is visible, hide it
        state[tableType].visibleColumns = visibleColumns.filter(
          (key) => key !== datakey
        );
      } else {
        // Column is hidden, show it
        state[tableType].visibleColumns.push(datakey);
      }
    },

    /**
     * Set visibility for multiple columns at once
     */
    setColumnVisibility: (state, action) => {
      const { tableType, datakey, isVisible } = action.payload;

      if (!state[tableType]) {
        console.error(`Invalid table type: ${tableType}`);
        return;
      }

      const visibleColumns = state[tableType].visibleColumns;
      const index = visibleColumns.indexOf(datakey);

      if (isVisible && index === -1) {
        // Make visible
        state[tableType].visibleColumns.push(datakey);
      } else if (!isVisible && index > -1) {
        // Make hidden
        state[tableType].visibleColumns = visibleColumns.filter(
          (key) => key !== datakey
        );
      }
    },

    /**
     * Add a custom column created by the user
     */
    addCustomColumn: (state, action) => {
      const { tableType, column } = action.payload;

      if (!state[tableType]) {
        console.error(`Invalid table type: ${tableType}`);
        return;
      }

      // Generate unique datakey if not provided
      // PRIORITIZE column.key to match API behavior
      const datakey =
        column.key ||
        column.id ||
        column.datakey ||
        `custom_${Date.now()}_${column.name
          .toLowerCase()
          .replace(/\s+/g, "_")}`;

      const newColumn = {
        ...column,
        datakey,
        isCustom: true,
        category: "Custom", // Ensure custom columns appear in Custom category
        createdAt: new Date().toISOString(),
        tooltip: column.description || column.tooltip,
      };

      // Add to custom columns (if not already there)
      const existingCustomIndex = state[tableType].customColumns.findIndex(
        (c) => c.datakey === datakey
      );
      if (existingCustomIndex > -1) {
        state[tableType].customColumns[existingCustomIndex] = newColumn;
      } else {
        state[tableType].customColumns.push(newColumn);
      }

      // Add to all columns and re-order
      const existingAllIndex = state[tableType].allColumns.findIndex(
        (c) => c.datakey === datakey
      );
      if (existingAllIndex > -1) {
        state[tableType].allColumns[existingAllIndex] = newColumn;
      } else {
        state[tableType].allColumns.push(newColumn);
      }

      state[tableType].allColumns = reorderColumnsInternally(
        state[tableType].allColumns
      );

      // Make it visible by default (if not already visible).
      // manager_review: custom columns are always hidden by default.
      if (
        tableType !== "manager_review" &&
        !state[tableType].visibleColumns.includes(datakey)
      ) {
        state[tableType].visibleColumns.push(datakey);
      }
    },

    /**
     * Set multiple custom columns from API response
     */
    setCustomColumns: (state, action) => {
      const { tableType, columns } = action.payload;

      if (!state[tableType]) {
        console.error(`Invalid table type: ${tableType}`);
        return;
      }

      // 1. Map API response to our internal column format
      const newCustomColumns = (columns || []).map((col) => {
        const nameSource = col.field_name || col.header || col.name || "";
        // PRIORITIZE col.key as it matches the data payload keys
        const datakey =
          col.key ||
          col.id?.toString() ||
          col.datakey ||
          `custom_${Date.now()}_${nameSource.toLowerCase().replace(/\s+/g, "_")}`;

        return {
          id: col.id,
          header: nameSource,
          type: col.field_type || col.type,
          mapping: col.value || col.mapping,
          options: col.options || [],
          datakey: datakey,
          key: col.key,
          isCustom: true,
          category: "Custom",
          name: col.name || nameSource,
          tooltip: col.description || col.tooltip,
          description: col.description,
        };
      });

      // 2. Rebuild allColumns according to this table's custom-order policy.
      // preserveCustomOrder (e.g. vacancy_analysis_full): a custom column that
      // still exists keeps its persisted (possibly interleaved-between-statics)
      // position and gets its latest backend definition; removed customs drop;
      // brand-new customs append. Legacy (default for every other table, incl.
      // Property View drill-downs): the exact pre-fix behavior — drop all old
      // customs and append the new custom set at the end.
      const policy = getColumnPolicy(tableType);
      if (policy.preserveCustomOrder) {
        const staticColumns = state[tableType].allColumns.filter(
          (c) => !c.isCustom
        );
        state[tableType].allColumns = reconcileColumns({
          currentColumns: [...staticColumns, ...newCustomColumns],
          persistedColumns: state[tableType].allColumns,
          terminalColumnKeys: TERMINAL_DATAKEYS,
          preserveCustom: false,
        });
      } else {
        state[tableType].allColumns = appendLegacyCustomColumns(
          state[tableType].allColumns,
          newCustomColumns
        );
      }
      // customColumns mirrors the new authoritative custom set.
      state[tableType].customColumns = newCustomColumns;

      // 5. Update visibleColumns list
      // First, remove any keys that no longer exist in the new columns list
      const allKeys = new Set(
        state[tableType].allColumns.map((c) => c.datakey)
      );
      state[tableType].visibleColumns = state[tableType].visibleColumns.filter(
        (key) => allKeys.has(key)
      );

      // Then, ensure all new custom columns are visible by default (unless it's manager_review)
      newCustomColumns.forEach((newCol) => {
        if (
          tableType !== "manager_review" &&
          !state[tableType].visibleColumns.includes(newCol.datakey)
        ) {
          state[tableType].visibleColumns.push(newCol.datakey);
        }
      });
    },

    /**
     * Clear all custom columns for a table
     */
    clearAllCustomColumns: (state, action) => {
      const { tableType } = action.payload;
      if (!state[tableType]) return;

      const customKeys = state[tableType].customColumns.map((c) => c.datakey);
      state[tableType].customColumns = [];
      state[tableType].allColumns = state[tableType].allColumns.filter(
        (c) => !customKeys.includes(c.datakey)
      );
      state[tableType].visibleColumns = state[tableType].visibleColumns.filter(
        (key) => !customKeys.includes(key)
      );
    },

    /**
     * Remove a custom column
     */
    removeCustomColumn: (state, action) => {
      const { tableType, datakey } = action.payload;

      if (!state[tableType]) {
        console.error(`Invalid table type: ${tableType}`);
        return;
      }

      // Remove from custom columns
      state[tableType].customColumns = state[tableType].customColumns.filter(
        (col) => col.datakey !== datakey
      );

      // Remove from all columns
      state[tableType].allColumns = state[tableType].allColumns.filter(
        (col) => col.datakey !== datakey
      );

      // Remove from visible columns
      state[tableType].visibleColumns = state[tableType].visibleColumns.filter(
        (key) => key !== datakey
      );
    },

    /**
     * Update an existing custom column
     */
    updateCustomColumn: (state, action) => {
      const { tableType, datakey, updates } = action.payload;

      if (!state[tableType]) {
        console.error(`Invalid table type: ${tableType}`);
        return;
      }

      // Update in custom columns
      const customIndex = state[tableType].customColumns.findIndex(
        (col) => col.datakey === datakey
      );
      if (customIndex > -1) {
        state[tableType].customColumns[customIndex] = {
          ...state[tableType].customColumns[customIndex],
          ...updates,
          updatedAt: new Date().toISOString(),
        };
      }

      // Update in all columns
      const allIndex = state[tableType].allColumns.findIndex(
        (col) => col.datakey === datakey
      );
      if (allIndex > -1) {
        state[tableType].allColumns[allIndex] = {
          ...state[tableType].allColumns[allIndex],
          ...updates,
          updatedAt: new Date().toISOString(),
        };
      }
    },

    /**
     * Show all columns
     */
    showAllColumns: (state, action) => {
      const { tableType } = action.payload;

      if (!state[tableType]) {
        console.error(`Invalid table type: ${tableType}`);
        return;
      }

      state[tableType].visibleColumns = state[tableType].allColumns.map(
        (col) => col.datakey
      );
    },

    /**
     * Hide all columns (except required ones)
     */
    hideAllColumns: (state, action) => {
      const { tableType, requiredColumns = [] } = action.payload;

      if (!state[tableType]) {
        console.error(`Invalid table type: ${tableType}`);
        return;
      }

      // Keep only required columns visible
      state[tableType].visibleColumns = requiredColumns;
    },

    /**
     * Reset column configuration to default
     */
    resetColumnConfig: (state, action) => {
      const { tableType } = action.payload;

      if (!state[tableType]) {
        console.error(`Invalid table type: ${tableType}`);
        return;
      }

      // Remove custom columns
      const staticColumns = state[tableType].allColumns.filter(
        (col) => !col.isCustom
      );

      state[tableType].customColumns = [];
      state[tableType].allColumns = staticColumns;
      state[tableType].visibleColumns = staticColumns.map((col) => col.datakey);
    },

    /**
     * Update global settings
     */
    updateSettings: (state, action) => {
      state.settings = {
        ...state.settings,
        ...action.payload,
      };
    },

    /**
     * Reorder columns based on drag-and-drop
     * Accepts either datakey-based or index-based reordering
     */
    reorderColumns: (state, action) => {
      const {
        tableType,
        sourceDatakey,
        destinationDatakey,
        sourceIndex,
        destinationIndex,
      } = action.payload;

      if (!state[tableType]) {
        console.error(`Invalid table type: ${tableType}`);
        return;
      }

      const columns = [...state[tableType].allColumns];

      // Support both datakey-based and index-based reordering
      let srcIdx, destIdx;

      if (sourceDatakey && destinationDatakey) {
        srcIdx = columns.findIndex((col) => col.datakey === sourceDatakey);
        destIdx = columns.findIndex(
          (col) => col.datakey === destinationDatakey
        );
      } else if (sourceIndex !== undefined && destinationIndex !== undefined) {
        srcIdx = sourceIndex;
        destIdx = destinationIndex;
      } else {
        console.error("Invalid reorder parameters");
        return;
      }

      if (srcIdx === -1 || destIdx === -1) {
        console.error("Column not found for reordering");
        return;
      }

      // Perform the reorder
      const [removed] = columns.splice(srcIdx, 1);
      columns.splice(destIdx, 0, removed);

      // Apply internal ordering rules to snap terminal columns back to the end
      const reorderedAllColumns = reorderColumnsInternally(columns);
      state[tableType].allColumns = reorderedAllColumns;

      // Update visible columns to maintain the new order
      state[tableType].visibleColumns = reorderedAllColumns
        .filter((col) => state[tableType].visibleColumns.includes(col.datakey))
        .map((col) => col.datakey);
    },
  },
});

// Export actions
export const {
  initializeColumns,
  toggleColumnVisibility,
  setColumnVisibility,
  addCustomColumn,
  setCustomColumns,
  clearAllCustomColumns,
  removeCustomColumn,
  updateCustomColumn,
  showAllColumns,
  hideAllColumns,
  resetColumnConfig,
  updateSettings,
  reorderColumns,
} = columnsSlice.actions;

// Selectors
export const selectColumnConfig = (state, tableType) =>
  state.columns[tableType];
export const selectVisibleColumns = (state, tableType) =>
  state.columns[tableType]?.visibleColumns || [];
export const selectAllColumns = (state, tableType) =>
  state.columns[tableType]?.allColumns || [];
export const selectCustomColumns = (state, tableType) =>
  state.columns[tableType]?.customColumns || [];
export const selectIsColumnVisible = (state, tableType, datakey) =>
  state.columns[tableType]?.visibleColumns.includes(datakey) || false;

export default columnsSlice.reducer;
